import React from 'react';

/**
 * Hook for managing dialog state
 */
export const useDialog = (initialOpen = false) => {
  const [open, setOpen] = React.useState(initialOpen);

  const openDialog = React.useCallback(() => setOpen(true), []);
  const closeDialog = React.useCallback(() => setOpen(false), []);
  const toggleDialog = React.useCallback(() => setOpen(prev => !prev), []);

  return {
    open,
    openDialog,
    closeDialog,
    toggleDialog,
    setOpen,
  };
};

/**
 * Hook for confirmation dialogs
 */
export const useConfirmDialog = () => {
  const [open, setOpen] = React.useState(false);
  const [config, setConfig] = React.useState<{
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({});

  const showConfirm = React.useCallback((newConfig: typeof config) => {
    setConfig(newConfig);
    setOpen(true);
  }, []);

  const handleConfirm = React.useCallback(() => {
    config.onConfirm?.();
    setOpen(false);
  }, [config]);

  const handleCancel = React.useCallback(() => {
    config.onCancel?.();
    setOpen(false);
  }, [config]);

  return {
    open,
    showConfirm,
    handleConfirm,
    handleCancel,
    config,
    setOpen,
  };
};

/**
 * Context type for confirmation dialogs
 */
export interface ConfirmContextType {
  showConfirm: (config: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }) => void;
}
